// UK Tax Calculation Utilities for 2025-2026 Tax Year

import { getBasicRateReduction, getAdditionalRevenue, getInitialSimulationState } from './taxSources';

// Tax savings calculation constants (from root taxCalculations.js)
export const CASH_SAVING_DENOMINATOR = 37400000;
export const PERCENTAGE_REDUCTION_DENOMINATOR = 475300000000;
export const BASIC_RATE_CUT_DENOMINATOR = 8250000000;

// Tax savings calculation functions (adapted from root taxCalculations.js)
export function calculateCashSavingPerPerson(additionalRevenue) {
  if (typeof additionalRevenue !== 'number' || isNaN(additionalRevenue)) {
    throw new Error('additionalRevenue must be a valid number');
  }
  return additionalRevenue / CASH_SAVING_DENOMINATOR;
}

export function calculatePercentageReductionInTaxBill(additionalRevenue) {
  if (typeof additionalRevenue !== 'number' || isNaN(additionalRevenue)) {
    throw new Error('additionalRevenue must be a valid number');
  }
  return (additionalRevenue / PERCENTAGE_REDUCTION_DENOMINATOR) * 100;
}

export function calculateBasicRateCutInPence(additionalRevenue) {
  if (typeof additionalRevenue !== 'number' || isNaN(additionalRevenue)) {
    throw new Error('additionalRevenue must be a valid number');
  }
  return additionalRevenue / BASIC_RATE_CUT_DENOMINATOR;
}

export function calculateAllTaxSavings(additionalRevenue) {
  return {
    cashSavingPerPerson: calculateCashSavingPerPerson(additionalRevenue),
    percentageReductionInTaxBill: calculatePercentageReductionInTaxBill(additionalRevenue),
    basicRateCutInPence: calculateBasicRateCutInPence(additionalRevenue)
  };
}

// Tax bands and thresholds
export const TAX_BANDS = {
  personalAllowance: 12570,
  basicRateLimit: 50270,
  higherRateLimit: 125140,
  personalAllowanceTaperThreshold: 100000,
};

export const TAX_RATES = {
  personalAllowance: 0,
  basic: 0.20,
  higher: 0.40,
  additional: 0.45,
};

// National Insurance thresholds (Class 1 Employee)
export const NI_THRESHOLDS = {
  primaryThreshold: 12570,
  upperEarningsLimit: 50270,
};

export const NI_RATES = {
  main: 0.08, // 8% between primary threshold and upper earnings limit
  upper: 0.02, // 2% above upper earnings limit
};

/**
  * Calculate income tax based on annual salary
  * @param {number} salary - Annual gross salary
  * @returns {object} - Breakdown of tax by band
  */
export function calculateIncomeTax(salary) {
  if (salary <= 0) {
    return {
      personalAllowanceTax: 0,
      basicRateTax: 0,
      higherRateTax: 0,
      additionalRateTax: 0,
      totalTax: 0,
      effectiveRate: 0,
    };
  }

  // Calculate adjusted personal allowance (tapers above £100k)
  let personalAllowance = TAX_BANDS.personalAllowance;
  if (salary > TAX_BANDS.personalAllowanceTaperThreshold) {
    const reduction = Math.floor((salary - TAX_BANDS.personalAllowanceTaperThreshold) / 2);
    personalAllowance = Math.max(0, personalAllowance - reduction);
  }

  // Calculate taxable income
  const taxableIncome = Math.max(0, salary - personalAllowance);

  // Calculate tax at each band
  let basicRateTax = 0;
  let higherRateTax = 0;
  let additionalRateTax = 0;

  // Basic rate: £personalAllowance to £50,270 (£37,700 band)
  const basicBandSize = TAX_BANDS.basicRateLimit - TAX_BANDS.personalAllowance;
  if (taxableIncome > 0) {
    basicRateTax = Math.min(taxableIncome, basicBandSize) * TAX_RATES.basic;
  }

  // Higher rate: £50,271 to £125,140
  const higherBandSize = TAX_BANDS.higherRateLimit - TAX_BANDS.basicRateLimit;
  if (salary > TAX_BANDS.basicRateLimit) {
    const incomeInHigherBand = Math.min(salary - TAX_BANDS.basicRateLimit, higherBandSize);
    higherRateTax = incomeInHigherBand * TAX_RATES.higher;
  }

  // Additional rate: over £125,140
  if (salary > TAX_BANDS.higherRateLimit) {
    const incomeInAdditionalBand = salary - TAX_BANDS.higherRateLimit;
    additionalRateTax = incomeInAdditionalBand * TAX_RATES.additional;
  }

  const totalTax = basicRateTax + higherRateTax + additionalRateTax;
  const effectiveRate = salary > 0 ? (totalTax / salary) * 100 : 0;

  return {
    personalAllowance,
    personalAllowanceTax: 0,
    basicRateTax: Math.round(basicRateTax * 100) / 100,
    higherRateTax: Math.round(higherRateTax * 100) / 100,
    additionalRateTax: Math.round(additionalRateTax * 100) / 100,
    totalTax: Math.round(totalTax * 100) / 100,
    effectiveRate: Math.round(effectiveRate * 100) / 100,
  };
}

/**
  * Calculate National Insurance contributions
  * @param {number} salary - Annual gross salary
  * @returns {object} - NI breakdown
  */
export function calculateNationalInsurance(salary) {
  if (salary <= 0 || salary <= NI_THRESHOLDS.primaryThreshold) {
    return {
      mainNI: 0,
      upperNI: 0,
      totalNI: 0,
    };
  }

  let mainNI = 0;
  let upperNI = 0;

  // Main rate: between primary threshold and upper earnings limit
  const mainBandIncome = Math.min(salary, NI_THRESHOLDS.upperEarningsLimit) - NI_THRESHOLDS.primaryThreshold;
  if (mainBandIncome > 0) {
    mainNI = mainBandIncome * NI_RATES.main;
  }

  // Upper rate: above upper earnings limit
  if (salary > NI_THRESHOLDS.upperEarningsLimit) {
    upperNI = (salary - NI_THRESHOLDS.upperEarningsLimit) * NI_RATES.upper;
  }

  return {
    mainNI: Math.round(mainNI * 100) / 100,
    upperNI: Math.round(upperNI * 100) / 100,
    totalNI: Math.round((mainNI + upperNI) * 100) / 100,
  };
}

/**
  * Calculate total deductions and take-home pay
  * @param {number} salary - Annual gross salary
  * @returns {object} - Complete breakdown
  */
export function calculateTakeHome(salary) {
  const tax = calculateIncomeTax(salary);
  const ni = calculateNationalInsurance(salary);
  const totalDeductions = tax.totalTax + ni.totalNI;
  const takeHome = salary - totalDeductions;
  const monthlyTakeHome = takeHome / 12;

  return {
    grossSalary: salary,
    incomeTax: tax,
    nationalInsurance: ni,
    totalDeductions: Math.round(totalDeductions * 100) / 100,
    takeHome: Math.round(takeHome * 100) / 100,
    monthlyTakeHome: Math.round((monthlyTakeHome) * 100) / 100,
  };
}

/**
 * Calculate total tax and NI for a given salary
 * @param {number} salary - Annual gross salary
 * @returns {number} - Total tax and NI
 */
export function calculateTotalTaxAndNI(salary) {
  const calc = calculateTakeHome(salary);
  return calc.totalDeductions;
}

/**
 * Calculate tax savings based on additional revenue sourced elsewhere
 * @param {number} salary - Annual gross salary
 * @param {number} extraBillions - Additional revenue in billions GBP
 * @returns {number} - Annual tax savings
 */
export function taxSavings(salary, extraBillions) {
  const totalTaxPot = 475300000000; // £475.3 billion
  const pctReduction = (extraBillions * 1000000000 / totalTaxPot) * 100;
  const personTax = calculateTotalTaxAndNI(salary);
  return personTax * (pctReduction / 100);
}

/**
  * Simulate reduced tax rates based on alternative revenue scenarios
  * Uses dynamic tax sources from tax_sources.json
  * @param {number} salary - Annual gross salary
  * @param {object} options - Simulation options (keys are tax source IDs, values are booleans)
  * @returns {object} - Simulated tax breakdown with savings
  */
export function simulateTaxReduction(salary, options = {}) {
  // Base calculation
  const baseCalc = calculateTakeHome(salary);

  // Get basic rate reduction and additional revenue from active simulations
  const basicRateReduction = getBasicRateReduction(options);
  const additionalRevenue = getAdditionalRevenue(options);

  // Convert additionalRevenue from billions to pounds for percentage calculation
  const additionalRevenueInPounds = additionalRevenue * 1000000000;
  
  // Calculate percentage reduction in total tax bill based on additional revenue
  // This applies the same percentage reduction to ALL tax bands proportionally
  const percentageReduction = calculatePercentageReductionInTaxBill(additionalRevenueInPounds) / 100;
  
  // Apply percentage reduction to total income tax (not just basic rate)
  // This ensures higher rate and additional rate taxpayers receive proportional benefits
  const totalTaxReduction = baseCalc.incomeTax.totalTax * percentageReduction;
  const simulatedTotalTax = baseCalc.incomeTax.totalTax - totalTaxReduction;
  
  // Calculate simulated basic rate for display purposes (legacy display value)
  const simulatedBasicRate = TAX_RATES.basic - basicRateReduction;
  
  // Calculate simulated basic rate tax using proportional reduction (consistent with total tax approach)
  // Note: This value reflects the proportional reduction, not what would be paid at simulatedBasicRate
  const basicTaxReduction = baseCalc.incomeTax.basicRateTax * percentageReduction;
  const simulatedBasicTax = baseCalc.incomeTax.basicRateTax - basicTaxReduction;
  
  const taxSavings = totalTaxReduction;
  const simulatedTakeHome = salary - simulatedTotalTax - baseCalc.nationalInsurance.totalNI;

  // Combine initial state (all false) with provided options to ensure all keys exist
  const activeSimulations = { ...getInitialSimulationState(), ...options };

  // Calculate tax savings metrics using the additional revenue in pounds
  const taxSavingsMetrics = calculateAllTaxSavings(additionalRevenueInPounds);

  return {
    original: baseCalc,
    simulated: {
      basicRate: Math.round(simulatedBasicRate * 100),
      basicRateTax: Math.round(simulatedBasicTax * 100) / 100,
      totalTax: Math.round(simulatedTotalTax * 100) / 100,
      takeHome: Math.round(simulatedTakeHome * 100) / 100,
      monthlyTakeHome: Math.round((simulatedTakeHome / 12) * 100) / 100,
    },
    savings: {
      annual: Math.round(taxSavings * 100) / 100,
      monthly: Math.round((taxSavings / 12) * 100) / 100,
    },
    additionalRevenue,
    activeSimulations,
    taxSavingsMetrics,
  };
}

/**
  * Get tax band data for visualization
  * @param {number} salary - Annual gross salary
  * @returns {array} - Array of band data for charts
  */
export function getTaxBandData(salary) {
  const calc = calculateTakeHome(salary);
  
  return [
    {
      name: 'Personal Allowance',
      range: '£0 - £12,570',
      rate: '0%',
      taxPaid: 0,
      amount: Math.min(salary, TAX_BANDS.personalAllowance),
      fill: '#4CAF50',
      isActive: salary > 0,
    },
    {
      name: 'Basic Rate',
      range: '£12,571 - £50,270',
      rate: '20%',
      taxPaid: calc.incomeTax.basicRateTax,
      amount: salary > TAX_BANDS.personalAllowance 
        ? Math.min(salary - TAX_BANDS.personalAllowance, TAX_BANDS.basicRateLimit - TAX_BANDS.personalAllowance)
        : 0,
      fill: '#2196F3',
      isActive: salary > TAX_BANDS.personalAllowance,
    },
    {
      name: 'Higher Rate',
      range: '£50,271 - £125,140',
      rate: '40%',
      taxPaid: calc.incomeTax.higherRateTax,
      amount: salary > TAX_BANDS.basicRateLimit
        ? Math.min(salary - TAX_BANDS.basicRateLimit, TAX_BANDS.higherRateLimit - TAX_BANDS.basicRateLimit)
        : 0,
      fill: '#FF9800',
      isActive: salary > TAX_BANDS.basicRateLimit,
    },
    {
      name: 'Additional Rate',
      range: '£125,140+',
      rate: '45%',
      taxPaid: calc.incomeTax.additionalRateTax,
      amount: salary > TAX_BANDS.higherRateLimit
        ? salary - TAX_BANDS.higherRateLimit
        : 0,
      fill: '#f44336',
      isActive: salary > TAX_BANDS.higherRateLimit,
    },
  ];
}

/**
  * Format currency for display
  * @param {number} amount - Amount to format
  * @returns {string} - Formatted currency string
  */
export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
  * Format percentage for display
  * @param {number} value - Percentage value
  * @returns {string} - Formatted percentage string
  */
export function formatPercentage(value) {
  return `${Math.round(value * 100) / 100}%`;
}